import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Zap, ListCheck, Clock, BookOpen, Award, Book } from "lucide-react";
import VideoDemoSection from "./VideoDemoSection";

interface BenefitCardProps {
  title: string;
  description: React.ReactNode;
  icon: React.ReactNode;
}

const BenefitCard = ({
  title,
  description,
  icon
}: BenefitCardProps) => {
  return (
    <Card className="card-hover">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-3">
          {icon}
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
        <p className="text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
};

const BenefitsSection = () => {
  const benefits = [
    {
      title: "AI-Optimized Tasks",
      description: "Every task comes pre-loaded with project context and optimized instructions, eliminating repetitive explanations to your AI agent",
      icon: <ListCheck className="h-5 w-5 text-coderide-purple" />
    },
    {
      title: "Persistent Memory",
      description: "Never lose architectural decisions between sessions. AI maintains full awareness of your project structure and design patterns",
      icon: <Book className="h-5 w-5 text-coderide-purple" />
    },
    {
      title: "Zero Context Loss",
      description: "Stop rebuilding project understanding from scratch. AI remembers your codebase evolution and maintains continuity",
      icon: <Clock className="h-5 w-5 text-coderide-purple" />
    },
    {
      title: "Pattern Recognition",
      description: "AI learns from your existing code patterns and conventions to provide suggestions that fit your project style",
      icon: <BookOpen className="h-5 w-5 text-coderide-purple" />
    },
    {
      title: "Consistent Quality",
      description: "Get reliable, contextually-aware AI suggestions that align with your project goals and coding standards",
      icon: <Award className="h-5 w-5 text-coderide-purple" />
    },
    {
      title: "Efficient Token Usage",
      description: "AI-enhanced tasks reduce redundant context sharing, minimizing token consumption while maximizing output quality",
      icon: <Zap className="h-5 w-5 text-coderide-purple" />
    }
  ];

  return (
    <>
      <section id="benefits" className="section-padding scroll-section">
        <div className="container">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
            Built for Developers Who Move Fast with AI
          </h2>
        <p className="text-center text-muted-foreground mb-8 max-w-3xl mx-auto text-base">
          Stop wasting time re-explaining your project. CodeRide <strong className="font-semibold text-gray-900">preserves context</strong> between sessions, letting you focus on building instead of managing <span className="bg-[#7867EE] text-white px-2 py-1 rounded font-mono">AI assistance</span>.{" "}
          <button 
            onClick={() => window.open('https://app.coderide.ai/auth/sign-up', '_blank')}
            className="inline-flex items-center text-base font-bold text-coderide-light-blue hover:text-coderide-dark-blue transition-colors"
          >
            <span className="underline">Try it free</span> →
          </button>
        </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <BenefitCard
                key={benefit.title}
                title={benefit.title}
                description={benefit.description}
                icon={benefit.icon}
              />
            ))}
          </div>
        </div>
      </section>

      <VideoDemoSection />
    </>
  );
};

export default BenefitsSection;
